import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SimpleModal } from '@/components/SimpleModal';
import { useToast } from '@/components/Toast';
import { ImageUploadButton, mediaUrl } from '@/components/ImageUploadButton';
import { SEGMENT_LIST } from '@/lib/segments';
import { Plus, Edit, Trash2, Quote } from 'lucide-react';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const empty = { id: null, nome: '', negocio: '', cidade: '', uf: 'SP', segmento: 'geral', texto: '', logo_url: null, ativo: true, ordem: 0 };

const TestimonialsAdminPanel = () => {
  const toast = useToast();
  const [itens, setItens] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setItens((await api.get('/admin/testimonials')).data.itens || []); } catch { toast.error('Erro ao carregar depoimentos'); }
  };
  useEffect(() => { load(); }, []);

  const salvar = async () => {
    if (!form.nome || !form.negocio || !form.cidade || !form.texto) { toast.error('Preencha nome, negócio, cidade e depoimento'); return; }
    setSaving(true);
    try {
      const { id, ...body } = form;
      body.ordem = Number(body.ordem) || 0;
      if (id) await api.put(`/admin/testimonials/${id}`, body); else await api.post('/admin/testimonials', body);
      toast.success('Depoimento salvo');
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  const excluir = async (t) => {
    if (!window.confirm(`Excluir depoimento de ${t.nome}?`)) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success('Excluído'); load(); } catch { toast.error('Erro ao excluir'); }
  };

  const toggle = async (t) => {
    const { id, created_at, updated_at, ...body } = t;
    try { await api.put(`/admin/testimonials/${id}`, { ...body, ativo: !t.ativo }); load(); } catch { toast.error('Erro ao atualizar'); }
  };

  const segNome = (k) => (k === 'geral' ? 'Geral (todas)' : SEGMENT_LIST.find((s) => s.key === k)?.nome || k);

  return (
    <Card data-testid="testimonials-admin-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><Quote className="w-5 h-5 text-amber-500" /> Depoimentos reais</CardTitle>
          <CardDescription>Exibidos nas páginas de vendas do SaaS (landing, segmentos e cidades). Quando houver depoimentos ativos, eles substituem os exemplos ilustrativos.</CardDescription>
        </div>
        <Button onClick={() => setForm({ ...empty })} className="bg-amber-600 hover:bg-amber-700" data-testid="testimonial-new-btn"><Plus className="w-4 h-4 mr-2" /> Novo</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Logo</TableHead><TableHead>Nome / Negócio</TableHead><TableHead>Cidade</TableHead><TableHead>Segmento</TableHead><TableHead>Depoimento</TableHead><TableHead>Ativo</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {itens.map((t) => (
              <TableRow key={t.id} data-testid={`testimonial-row-${t.id}`}>
                <TableCell>{t.logo_url ? <img src={mediaUrl(t.logo_url)} alt="" className="w-9 h-9 rounded object-cover border" /> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell><div className="font-medium">{t.nome}</div><div className="text-xs text-slate-500">{t.negocio}</div></TableCell>
                <TableCell className="text-sm">{t.cidade}/{t.uf}</TableCell>
                <TableCell className="text-sm">{segNome(t.segmento)}</TableCell>
                <TableCell className="text-sm text-slate-600 max-w-xs truncate">{t.texto}</TableCell>
                <TableCell><Switch checked={!!t.ativo} onCheckedChange={() => toggle(t)} data-testid={`testimonial-toggle-${t.id}`} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setForm({ ...empty, ...t })} data-testid={`testimonial-edit-${t.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => excluir(t)} data-testid={`testimonial-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {itens.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Nenhum depoimento cadastrado. As landings exibem exemplos ilustrativos até você cadastrar o primeiro.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>

      <SimpleModal open={!!form} onClose={() => setForm(null)} title={form?.id ? 'Editar depoimento' : 'Novo depoimento'}>
        {form && (
          <div className="space-y-4" data-testid="testimonial-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome do cliente</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="testimonial-nome" /></div>
              <div><Label>Negócio</Label><Input value={form.negocio} onChange={(e) => setForm({ ...form, negocio: e.target.value })} placeholder="Ex: Açougue do Zé" data-testid="testimonial-negocio" /></div>
              <div><Label>Cidade</Label><Input value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} data-testid="testimonial-cidade" /></div>
              <div><Label>UF</Label>
                <select className="w-full h-10 border rounded-md px-3 text-sm bg-white" value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value })} data-testid="testimonial-uf">
                  {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div><Label>Segmento</Label>
                <select className="w-full h-10 border rounded-md px-3 text-sm bg-white" value={form.segmento} onChange={(e) => setForm({ ...form, segmento: e.target.value })} data-testid="testimonial-segmento">
                  <option value="geral">Geral (todas as páginas)</option>
                  {SEGMENT_LIST.map((s) => <option key={s.key} value={s.key}>{s.nome}</option>)}
                </select>
              </div>
              <div><Label>Ordem</Label><Input type="number" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: e.target.value })} /></div>
            </div>
            <div><Label>Depoimento</Label><Textarea rows={3} value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} data-testid="testimonial-texto" /></div>
            <div className="flex items-center justify-between gap-4">
              <ImageUploadButton value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} pasta="depoimentos" label="logo" testId="testimonial-logo" />
              <div className="flex items-center gap-2"><Label>Ativo</Label><Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              <Button onClick={salvar} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="testimonial-save-btn">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </div>
          </div>
        )}
      </SimpleModal>
    </Card>
  );
};

export default TestimonialsAdminPanel;
